<template>
  <el-dialog
    :visible.sync="visible"
    title=""
    width="100%"
    fullscreen
    :close-on-click-modal="false"
    :show-close="false"
    custom-class="sign-confirm-dialog"
    @close="handleClose"
  >
    <!-- Header -->
    <div class="sign-confirm__header">
      <p class="sign-confirm__title">Confirmar firma</p>
      <button class="sign-confirm__close" @click="handleClose">
        <i class="el-icon-close" />
      </button>
    </div>

    <!-- Content -->
    <div class="sign-confirm__body">
      <div class="sign-confirm__card">
        <!-- Signer info -->
        <div class="sign-confirm__info" v-if="signer">
          <p class="sign-confirm__signer-name">{{ signer.name }}</p>
          <p class="sign-confirm__signer-email">{{ signer.email }}</p>
        </div>

        <!-- Document name -->
        <div class="sign-confirm__doc-name" v-if="documentName">
          <span class="sign-confirm__label">Documento:</span>
          {{ documentName }}
        </div>

        <!-- Tabs: drawn vs typed -->
        <div class="sign-confirm__tabs">
          <button
            class="sign-confirm__tab"
            :class="{ 'sign-confirm__tab--active': mode === 'drawn' }"
            @click="mode = 'drawn'"
          >
            <i class="el-icon-edit" /> Dibujar
          </button>
          <button
            class="sign-confirm__tab"
            :class="{ 'sign-confirm__tab--active': mode === 'typed' }"
            @click="mode = 'typed'"
          >
            <i class="el-icon-document" /> Escribir nombre
          </button>
        </div>

        <!-- Drawn signature -->
        <div v-show="mode === 'drawn'" class="sign-confirm__canvas-area">
          <SignatureCanvas
            ref="signatureCanvas"
            :height="200"
            @save="onCanvasSave"
            @clear="signatureData = null"
          />
        </div>

        <!-- Typed signature -->
        <div v-show="mode === 'typed'" class="sign-confirm__typed-area">
          <el-input
            v-model="typedName"
            placeholder="Escribe tu nombre completo"
            size="medium"
            @input="generateTypedSignature"
          />
          <div
            class="sign-confirm__typed-preview"
            v-if="typedName"
            ref="typedPreview"
          >
            {{ typedName }}
          </div>
        </div>

        <!-- Legal acceptance -->
        <div class="sign-confirm__legal">
          <el-checkbox v-model="accepted">
            Acepto que esta firma electrónica tiene validez legal y representa
            mi consentimiento para firmar este documento.
          </el-checkbox>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="sign-confirm__footer">
      <button class="btn md__btn-third-secondary" @click="handleClose">
        Cancelar
      </button>
      <button
        class="btn md__btn-primary"
        :disabled="!canConfirm || submitting"
        @click="confirm"
      >
        <i class="el-icon-loading" v-if="submitting" />
        Firmar documento
      </button>
    </div>
  </el-dialog>
</template>

<script>
import SignatureCanvas from "./SignatureCanvas";

export default {
  name: "SignConfirmModal",
  components: { SignatureCanvas },
  props: {
    show: { type: Boolean, default: false },
    signer: { type: Object, default: null },
    documentName: { type: String, default: "" },
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      mode: "drawn",
      typedName: "",
      signatureData: null,
      accepted: false
    };
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit("update:show", val);
      }
    },
    canConfirm() {
      if (!this.accepted) return false;
      if (this.mode === "drawn") return !!this.signatureData;
      if (this.mode === "typed") return this.typedName.trim().length >= 2;
      return false;
    }
  },
  methods: {
    onCanvasSave(dataUrl) {
      this.signatureData = dataUrl;
    },
    generateTypedSignature() {
      // Typed signature uses the name directly — the backend renders it
      this.signatureData = null;
    },
    confirm() {
      if (!this.canConfirm) return;

      let payload = {
        method: this.mode,
        accepted: this.accepted
      };

      if (this.mode === "drawn") {
        // Get latest data from canvas
        if (this.$refs.signatureCanvas) {
          this.signatureData = this.$refs.signatureCanvas.getDataUrl();
        }
        payload.signature_image = this.signatureData;
      } else {
        payload.typed_name = this.typedName.trim();
      }

      this.$emit("confirm", payload);
    },
    handleClose() {
      this.visible = false;
      this.$emit("cancel");
    },
    reset() {
      this.mode = "drawn";
      this.typedName = "";
      this.signatureData = null;
      this.accepted = false;
      if (this.$refs.signatureCanvas) {
        this.$refs.signatureCanvas.clear();
      }
    }
  },
  watch: {
    show(val) {
      if (val) this.reset();
    }
  }
};
</script>

<style lang="scss" scoped>
.sign-confirm-dialog {
  ::v-deep .el-dialog__header {
    display: none;
  }
  ::v-deep .el-dialog__body {
    padding: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }
}

.sign-confirm {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid #e4eaf6;
    background: #fff;
    flex-shrink: 0;
  }
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #31355c;
    margin: 0;
  }
  &__close {
    background: #31355c;
    color: #fff;
    border: none;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    &:hover {
      opacity: 0.85;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 30px 24px;
    background: #f7f8f9;
    display: flex;
    justify-content: center;
  }
  &__card {
    background: #fff;
    border: 1px solid #e4eaf6;
    border-radius: 16px;
    padding: 28px;
    width: 100%;
    max-width: 560px;
    box-shadow: 0 0 10px 0 #d6e4f3;
    align-self: flex-start;
  }

  &__info {
    margin-bottom: 20px;
    text-align: center;
  }
  &__signer-name {
    font-size: 16px;
    font-weight: 600;
    color: #31355c;
    margin: 0 0 2px;
  }
  &__signer-email {
    font-size: 13px;
    color: #989aae;
    margin: 0;
  }

  &__doc-name {
    font-size: 13px;
    color: #31355c;
    background: #f7f8f9;
    padding: 10px 14px;
    border-radius: 10px;
    margin-bottom: 20px;
  }
  &__label {
    color: #989aae;
    margin-right: 4px;
  }

  &__tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
  }
  &__tab {
    flex: 1;
    border: 1.5px solid #e4eaf6;
    background: #fff;
    color: #989aae;
    font-size: 13px;
    font-weight: 500;
    padding: 10px 16px;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
      border-color: #3fdbbf;
    }
    &--active {
      border-color: #3fdbbf;
      background: #f0fdf9;
      color: #31355c;
    }
    i {
      margin-right: 4px;
    }
  }

  &__canvas-area {
    margin-bottom: 20px;
  }

  &__typed-area {
    margin-bottom: 20px;
    ::v-deep .el-input__inner {
      border: 1.5px solid #e4eaf6;
      border-radius: 10px;
      background: #fafbfd;
      font-size: 14px;
      min-height: 44px;
      &:focus {
        border-color: #3fdbbf;
        background: #fff;
      }
    }
  }
  &__typed-preview {
    margin-top: 16px;
    padding: 20px;
    text-align: center;
    font-family: "Dancing Script", "Brush Script MT", cursive;
    font-size: 32px;
    color: #31355c;
    border: 1.5px dashed #e4eaf6;
    border-radius: 12px;
    background: #fafbfd;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__legal {
    margin-bottom: 8px;
    ::v-deep .el-checkbox__label {
      font-size: 12px;
      color: #989aae;
      white-space: normal;
      line-height: 1.5;
    }
    ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: #3fdbbf;
      border-color: #3fdbbf;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid #e4eaf6;
    background: #fff;
    flex-shrink: 0;
  }
}
</style>
